import React, { useContext, useEffect, useState } from 'react';
import { Button, ButtonGroup, Card, CardBody, CardTitle, Col, Collapse, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { MdArrowRight, MdOutlineExpandLess, MdOutlineExpandMore } from "react-icons/md";
import { useNavigate, useParams } from 'react-router-dom';
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import _ from 'lodash';
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, UserContext, storage } from '../utils';

export default function MoveOrder(props) {
   const ICONS = {
      collapseIcon: <MdOutlineExpandLess className="mobileToggle align-self-center" />,
      expandIcon: <MdOutlineExpandMore className="mobileToggle align-self-center" />,
      arrowIcon: <MdArrowRight className="mobileToggleEx align-self-center" />
   }
   const { id } = useParams();
   const { currentUser } = useContext(UserContext);
   const [barcode, setBarcode] = useState(null);
   const [data, setData] = useState({});
   const [expanded, setExpanded] = useState(null);
   const [loaded, setLoaded] = useState(false);
   const [manualEntry, setManual] = useState(null);
   const [open, setOpen] = useState(null);
   const [spin, setSpinner] = useState(true);
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   const navigate = useNavigate();

   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;

   useEffect(() => {
      if (!loaded && id) {
         api
            .post(`Transfer/GetMoveOrder/${id}`)
            .then(response => {
               if (response.data.success) {
                  setData(response.data.data);
               }
               else {
                  setData({});
                  toast.error(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setLoaded(true);
               setSpinner(false);
               inputFocus("searchInput");
            });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   useEffect(() => {
      // if device has built-in scanner see if a scan was completed
      if (barcode && hasScanner && !manualEntry) {
         setLoaded(false);
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [barcode])

   useEffect(() => {
      if (!loaded && barcode) {
         // If a QR code was scanned then parse out just the barcode from the data
         var code = constants.GetRealBarcode(barcode);
         setSpinner(true);
         api
            .post(`Transfer/AddMoveCase/${id}/${code}`)
            .then(response => {
               if (response.data.success) {
                  setData(response.data.data);
                  setBarcode(null);
               }
               else {
                  toast.error(response.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setSpinner(false);
               setLoaded(true);
               inputFocus("searchInput");
            });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }

      if (!barcode) {
         inputFocus("searchInput");
         return;
      }

      setLoaded(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   function completeMove() {
      if (!data.lineItems || !data.lineItems.length > 0) {
         toast.error("You cannot complete a move order with no line items\n\nTo cancel, please delete the move order from the main Move Orders screen.");
         return;
      }
      setSpinner(true);
      setBarcode(null);
      api
         .post(`Transfer/CompleteMoveOrder/${id}`)
         .then((response) => {
            if (response.data.success) {
               navigate(constants.ENDPOINTS.moveOrders);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            setData({});
         })
         .finally(() => {
            setSpinner(false);
            inputFocus("searchInput");
         })
   }

   const handleKeypress = e => {
      // On key press for search input field
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   async function lookup(barcode) {
      setBarcode(barcode);

      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }
      inputFocus("searchInput");
   }

   function removeCase(moveId, detailId) {
      confirmAlert({
         title: "Delete Move Order Case",
         message: "Are you sure you want to remove this case?",
         buttons: [
            {
               label: "Ok",
               onClick: () => {
                  setSpinner(true);
                  api
                     .post(`Transfer/DeleteMoveCase/${moveId}/${detailId}`)
                     .then((response) => {
                        if (response.data.success) {
                           setData(response.data.data);
                        } else {
                           toast.error(response.data.message);
                        }
                     })
                     .catch(api.catchHandler)
                     .finally(() => {
                        setSpinner(false);
                        inputFocus("searchInput");
                     });
               },
            },
            {
               label: "Cancel",
               onClick: () => {
                  inputFocus("searchInput");
               },
            },
         ],
      });
   }

   const toggle = (idx) => {
      if (!expanded || idx !== expanded) {
         setExpanded(idx);
      } else if (expanded === idx) {
         setExpanded(null);
      }
      inputFocus("searchInput");
   }

   return (
      <>
         <DesktopContainer screenName="Move Order">
            <ServiceThreadSpinner on={spin}>
               <Row><Col className="h6 fw-bold" xs="12">Scan an inventory label to begin</Col></Row>
               <Row><Col><hr className="mb-0 my-1" /></Col></Row>
               <br />
               {!hasScanner && isMobile ?
                  <div>
                     <Row className="justify-content-center">
                        <Col xs="12">
                           <Button
                              title="scan entry"
                              className="shredButton w-100 btn-md"
                              onClick={() => setOpen(1)}>
                              Scan Barcode
                           </Button>
                        </Col>
                     </Row>
                     <Row className="lineheight8">&nbsp;</Row>
                  </div>
                  : null
               }
               <div>
                  <Row className="justify-content-center">
                     <Col xs="12">
                        <Button
                           title="manual entry"
                           className="shredButton w-100 btn-md"
                           onClick={() => setManual(1)}>
                           Manual Entry
                        </Button>
                     </Col>
                  </Row>
                  {isMobile ?
                     <div>
                        <Row className="lineheight8">&nbsp;</Row>
                        <Row className="justify-content-center">
                           <Col xs="12">
                              <Input
                                 name="searchInput"
                                 placeholder="Focus here to scan barcode"
                                 className="h-100"
                                 onChange={(e) => lookup(e.target.value)}
                              />
                           </Col>
                        </Row>
                     </div>
                     : null
                  }
               </div>
               {loaded && data && data.lineItems && data.lineItems.length > 0 ?
                  <div>
                     <Row><Col><hr className="my-0" /></Col></Row>
                     <br />
                     <Row><Col className="h5" xs="12">{data.headerDisplay}</Col></Row>
                     {_.map(data.lineItems, (item, index) => {
                        return (
                           <div details={item} key={index} >
                              <Card id="toggler" className="py-0" onClick={() => toggle(index)} style={{ cursor: "pointer" }}>
                                 <CardBody className={(index % 2 === 0) ? "mobileBlueBg text-white my-0 py-2 px-2" : "mobileBlueBgAlt text-white my-0 py-2 px-2"}>
                                    <CardTitle className={(index % 2 === 0) ? "mobileBlueBg text-white my-0 py-0 px-0" : "mobileBlueBgAlt text-white my-0 py-0 px-0"}>
                                       {(expanded === index) ? ICONS.collapseIcon : ICONS.expandIcon} {item.itemWithDesc}
                                    </CardTitle>
                                    {item.cases && item.cases.length > 0 ?
                                       <Row>
                                          <Col xs="12">
                                             <Collapse className="text-white mx-0 py-0 px-0" toggler="#toggler" isOpen={expanded === index}>
                                                {_.map(item.cases, (lot, idx) => {
                                                   return (
                                                      <div details={lot} key={idx}>
                                                         {ICONS.arrowIcon} {lot.lotNumber}&nbsp;&nbsp;
                                                         <Button children="REMOVE" className="btn btn-sm" onClick={() => {
                                                            removeCase(lot.moveId, lot.detailId);
                                                         }} />
                                                      </div>
                                                   )
                                                })}
                                             </Collapse>
                                          </Col>
                                       </Row>
                                       : null
                                    }
                                 </CardBody>
                              </Card>
                           </div>
                        )
                     })}
                  </div>
                  : null
               }
               <br />
               <div>
                  <Row><Col><hr className="my-0" /></Col></Row>
                  {data && data.lineItems && data.lineItems.length > 0 ?
                     <Row>
                        <Col xs="12">
                           <Button
                              title="Save"
                              className="shredButton w-100 btn-md my-3"
                              onClick={() => completeMove()}>
                              Complete Move Order
                           </Button>
                        </Col>
                     </Row>
                     : null
                  }
                  <Row>
                     <Col xs="12">
                        <Button
                           title="To Search"
                           className="btn w-100 btn-md my-3"
                           onClick={() => navigate(constants.ENDPOINTS.moveOrders)}>
                           Close
                        </Button>
                     </Col>
                  </Row>
               </div>


               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text);
                                 setOpen(null);
                                 setLoaded(false);
                                 inputFocus("searchInput");
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           // onError={(err) => console.error("error", err)}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }
                        }>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value)}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button children="Retrieve" className="button-success" onClick={() => {
                           setManual(null);
                        }} />
                        <Button children="Cancel" className="button-danger" onClick={() => {
                           setBarcode(null);
                           setManual(null);
                        }} />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
